<template>
  <button
    class="btn"
    :type="type"
    :class="[
      `btn-${color}`,
      {
        'btn-small': small,
        'btn-ghost': ghost,
        blank,
        inline,
        icon,
        borderless,
        disabled,
        hovergrow,
        'btn-stayactive': stayActive,
        bigicon
      }
    ]"
    @click="callback"
  >
      <slot name="icon" class="icon"></slot>
    <slot />
  </button>
</template>

<script>
export default {
  name: 'BosButton',
  props: {
    bigicon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'submit'
    },
    ghost: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    blank: {
      type: Boolean,
      default: false
    },
    hovergrow: {
      type: Boolean,
      default: true
    },
    color: {
      type: String
    },
    inline: Boolean,
    small: Boolean,
    icon: Boolean,
    borderless: Boolean,
    stayActive: Boolean
  },
  methods: {
    callback: function(e) {
      this.$emit('click', e)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/mixins/button.scss';

$buttonSizeNormal: (
  'class': '',
  'font-size': 1rem,
  'font-weight': $font-weight-bold,
  'padding': 0.65em 2vw,
  'border-width': 2px
) !default;

$buttonSizeSmall: (
  'class': 'small',
  'font-size': 0.8rem,
  'font-weight': $font-weight-bold,
  'padding': 0.5em 1.6vw,
  'border-width': 2px
) !default;

$buttonColorDefault: (
  'class': '',
  'bg': $brand-primary,
  'border-color': $brand-primary,
  'color': $brand-white,  
  'hover-bg': darken($brand-primary, 10%),
  'hover-border-color': darken($brand-primary, 10%),
  'hover-color': $brand-white,
  'text-transform': uppercase
) !default;

$buttonColorGhost: (
  'class': 'ghost',
  'bg': transparent,
  'border-color': $brand-primary,
  'color': $brand-primary,
  'hover-bg': $brand-primary,
  'hover-border-color': $brand-primary,
  'hover-color': $brand-white,
  'text-transform': uppercase
) !default;

$buttonColorRed: (
  'class': 'red',
  'bg': $brand-red,
  'border-color': $brand-red,
  'color': $brand-white,
  'hover-bg': darken($brand-red, 10%),
  'hover-border-color': darken($brand-red, 10%),
  'hover-color': $brand-white,
  'text-transform': uppercase
) !default;

$buttonColorWhite: (
  'class': 'white',
  'bg': $brand-white,
  'border-color': $brand-white,
  'color': $brand-blue,
  'hover-bg': $brand-blue,
  'hover-border-color': $brand-blue,
  'hover-color': $brand-white,
  'text-transform': uppercase
) !default;

$buttonColorWhite2: (
  'class': 'white2',
  'bg': transparent,
  'border-color': $brand-white,
  'color': $brand-white,
  'hover-bg': $brand-white,
  'hover-border-color': $brand-white,
  'hover-color': $brand-blue,
  'text-transform': uppercase
) !default;

$buttonColorWhiteOnRed: (
  'class': 'white-on-red',
  'bg': $brand-red,
  'border-color': $brand-white,
  'color': $brand-white,
  'hover-bg': $brand-white,
  'hover-border-color': $brand-white,
  'hover-color': $brand-red,
  'text-transform': uppercase
) !default;

$buttonColorPrimary: (
  'class': 'primary',
  'bg': $brand-primary,
  'border-color': $brand-white,
  'color': $brand-white,
  'hover-bg': $brand-white,
  'hover-border-color': $brand-primary,
  'hover-color': $brand-primary,
  'text-transform': uppercase
) !default;

$button-transition: all 200ms ease-in-out !default;

button {
  display: inline-block;
  padding: 0;
  background: none;
  border: 0 solid transparent;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  line-height: 1;
  white-space: nowrap;
  transition: $button-transition;
  border-radius: 10px;

  @include buttonSizeVariant($buttonSizeNormal);
  @include buttonSizeVariant($buttonSizeSmall);

  @include buttonColorVariant($buttonColorDefault);
  @include buttonColorVariant($buttonColorGhost);
  @include buttonColorVariant($buttonColorRed);
  @include buttonColorVariant($buttonColorWhite);
  @include buttonColorVariant($buttonColorWhite2);
  @include buttonColorVariant($buttonColorWhiteOnRed);
  @include buttonColorVariant($buttonColorPrimary);

  img {
    display: inline-block;
    margin-right: 0.4em;
    width: 1em;
    height: auto;
    vertical-align: middle;
  }

  &.borderless {
    border: 0 none !important;
  }

  &.blank {
    padding: 0 !important;
    background: none !important;
    border: 0 none !important;
    color: inherit !important;
  }

  &.inline {
    + .btn {
      margin-left: 0.3em;
    }
  }

  &:not(.icon) {
    svg {
      width: 2rem;
      height: 2rem;
      margin: -0.5rem 0.5rem -0.5rem -0.5rem !important;
    }
  }

  &.bigicon {
    &:not(.icon) {
    svg {
      width: 2rem;
      height: 2rem;
      margin: -0.5rem 0.8rem -0.5rem -0.5rem !important;
      transform: scale(1.4)
    }
  }

  }

  &.icon {
    padding: 0.333em;
    width: 2.333em;
    height: 2.333em;

    + .btn {
      margin-left: 0.6em;
    }
  }

  &.disabled {
    opacity: 0.2;
    filter: grayscale(1);
    cursor: not-allowed;
  }

  &.hovergrow {
    transition: all .2s ease-in-out;

    &:hover {
      transform: scale(1.1);
    }
  }
}
</style>
